import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Select } from '@/components/common';
import { useInsuranceGroupsLookup, useInsuranceSubgroupsLookup } from '@/hooks/useProducts';
import { GENERIC_BUSINESS_TYPE_OPTIONS } from '../../genericSchema';

interface GenericDetailsStepProps {
  /** Selected InsuranceType UUID; scopes the Group dropdown. */
  insuranceTypeId?: string;
}

/**
 * The fixed part of the generic flow: the `QuoteRequest` columns that exist for
 * every insurance line, regardless of what the field schema says.
 *
 * Group/Subgroup are not just classification -- `InsuranceTypeFieldSchema.resolve`
 * narrows on them, so choosing a group can add or override the fields the later
 * steps render (most-specific scope wins on a key collision). That is why they
 * live BEFORE the schema-driven steps rather than on the review screen.
 */
export function GenericDetailsStep({ insuranceTypeId }: GenericDetailsStepProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const group = watch('insurance_group') as string | undefined;

  const { data: groups, isLoading: groupsLoading } = useInsuranceGroupsLookup(insuranceTypeId);
  const { data: subgroups, isLoading: subgroupsLoading } = useInsuranceSubgroupsLookup(
    group || undefined
  );

  // `Select` renders its placeholder as a DISABLED option, so an explicit
  // "None" row is the only way to clear an optional classification once set.
  const groupOptions = useMemo(
    () => [
      { value: '', label: groupsLoading ? 'Loading…' : 'None' },
      ...(groups ?? []).map((g) => ({ value: g.id, label: g.name })),
    ],
    [groups, groupsLoading]
  );
  const subgroupOptions = useMemo(
    () => [
      { value: '', label: subgroupsLoading ? 'Loading…' : 'None' },
      ...(subgroups ?? []).map((s) => ({ value: s.id, label: s.name })),
    ],
    [subgroups, subgroupsLoading]
  );

  // A subgroup belongs to exactly one group, so a leftover subgroup from a
  // previous group would resolve the field schema against a scope the user is
  // no longer in. Fires on any change of group, not just on clearing it.
  const previousGroup = useRef(group);
  useEffect(() => {
    if (previousGroup.current === group) return;
    previousGroup.current = group;
    setValue('insurance_subgroup', '');
  }, [group, setValue]);

  const errorFor = (key: string) =>
    (errors as Record<string, { message?: string } | undefined>)[key]?.message;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quote Details</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        How this quote should be classified. Group and sub-group are optional, but choosing them
        can tailor the fields on the next steps.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label="Business Type"
          options={GENERIC_BUSINESS_TYPE_OPTIONS}
          placeholder="Select business type"
          required
          error={errorFor('business_type')}
          {...register('business_type')}
        />
        <Select
          label="Group"
          options={groupOptions}
          helperText="Optional"
          error={errorFor('insurance_group')}
          {...register('insurance_group')}
        />
        <Select
          label="Sub-group"
          options={subgroupOptions}
          helperText={group ? 'Optional' : 'Choose a group first'}
          disabled={!group}
          error={errorFor('insurance_subgroup')}
          {...register('insurance_subgroup')}
        />
      </div>

      <div className="mt-4">
        <label
          htmlFor="generic-quote-notes"
          className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
        >
          Notes
        </label>
        <textarea
          id="generic-quote-notes"
          rows={3}
          placeholder="Anything the insurer or your team should know about this quote"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          {...register('notes')}
        />
      </div>
    </div>
  );
}

export default GenericDetailsStep;
